import type { RiskBand } from "./TrendLine";

type RecordType = Record<string, string | number | boolean | null>;

type TrendFieldMapping = {
  scoreField: string;
  statusField: string;
  currentScoreField: string;
};

export type TrendData = {
  trendScores: number[];
  trendStatuses: RiskBand[];
};

export type BandThresholds = {
  greenMax: number;
  yellowMax: number;
  maxScore: number;
};

function mapStatusToken(value: string | undefined): RiskBand {
  const trimmed = value?.trim().toUpperCase();

  if (trimmed === "G") return "Green";
  if (trimmed === "Y") return "Yellow";
  if (trimmed === "R") return "Red";

  return "Green";
}

function getValidNumericValues<T extends RecordType>(
  records: T[],
  fieldName: string,
): number[] {
  return records
    .map((r) => r[fieldName])
    .filter((value) => {
      return (
        value !== null &&
        value !== undefined &&
        typeof value !== "boolean" &&
        value !== ""
      );
    })
    .map((value) => Number(value))
    .filter((score) => !Number.isNaN(score));
}

export function extractTrendData<T extends RecordType>(
  record: T,
  fieldMapping: TrendFieldMapping,
): TrendData {
  const scoreTokens = record[fieldMapping.scoreField]
    ? String(record[fieldMapping.scoreField]).split(",")
    : [];
  const statusTokens = record[fieldMapping.statusField]
    ? String(record[fieldMapping.statusField]).split(",")
    : [];

  const pairedTrendData = scoreTokens.reduce<{
    trendScores: number[];
    trendStatuses: RiskBand[];
  }>(
    (accumulator, scoreToken, index) => {
      const trimmedScore = scoreToken.trim();

      if (trimmedScore === "") {
        return accumulator;
      }

      const score = Number(trimmedScore);
      if (Number.isNaN(score)) {
        return accumulator;
      }

      accumulator.trendScores.push(score);
      accumulator.trendStatuses.push(mapStatusToken(statusTokens[index]));

      return accumulator;
    },
    { trendScores: [], trendStatuses: [] },
  );

  return pairedTrendData;
}

export function calculateBandThresholds<T extends RecordType>(
  records: T[],
  fieldMapping: Pick<TrendFieldMapping, "currentScoreField">,
): BandThresholds {
  const scores = getValidNumericValues(records, fieldMapping.currentScoreField);

  if (scores.length === 0) {
    return { greenMax: 10, yellowMax: 10, maxScore: 30 };
  }

  const greenRecords = records.filter((r) => r.band === "Green");
  const yellowRecords = records.filter((r) => r.band === "Yellow");

  const greenScores = getValidNumericValues(
    greenRecords,
    fieldMapping.currentScoreField,
  );
  const yellowScores = getValidNumericValues(
    yellowRecords,
    fieldMapping.currentScoreField,
  );

  const greenMax = greenScores.length > 0 ? Math.max(...greenScores) : 10;
  const yellowMax = yellowScores.length > 0 ? Math.max(...yellowScores) : 10;
  const maxScore = Math.max(...scores);

  return { greenMax, yellowMax, maxScore };
}

export const RISK_TREND_FIELDS: TrendFieldMapping = {
  scoreField: "longitudinal_trend_of_risk_score",
  statusField: "longitudinal_risk_status_trajectory",
  currentScoreField: "assessed_risk_score",
};

export const SIGNAL_TREND_FIELDS: TrendFieldMapping = {
  scoreField: "longitudinal_signal_data",
  statusField: "longitudinal_signal_status",
  currentScoreField: "current_score",
};
